import { useState, useEffect } from 'react';
import { DashboardContentShell, KPIBar, DashboardSection, DashboardWidget } from '../shared/layout/DashboardLayout';
import { MetricCard } from '../shared/layout/CardLayout';
import { RCAAnalysisForm, CAPAWorkflow } from '../shared/qms';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { NCRStatus, CAPAType, CAPA } from '@cbm/shared';
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  TrendingDown, 
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  ArrowRight,
  Shield,
  Plus,
  X
} from 'lucide-react';

interface NCRSummary {
  ncrId: string;
  machineId: string;
  status: NCRStatus;
  openedAt: string;
  impactSummary: string;
  triggerAlertIds: string[];
  capaIds: string[];
  closedAt?: string;
}

interface CAPASummary {
  capaId: string;
  ncrId: string;
  type: CAPAType;
  description: string;
  assignedTo: string;
  dueDate: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'COMPLETED' | 'CLOSED';
  createdAt: string;
  completedAt?: string;
}

interface RCAAnalysis {
  method: '5-why' | 'fishbone' | 'fault-tree';
  analysis: any;
}

interface TraceabilityItem {
  id: string;
  type: 'alert' | 'ncr' | 'rca' | 'capa' | 'closure';
  timestamp: string;
  title: string;
  description: string;
  status: string;
  machineId: string;
  assignedTo?: string;
}

interface QualityMetrics {
  totalNCRs: number;
  openNCRs: number;
  closedNCRs: number;
  totalCAPAs: number;
  overdueCAPAs: number;
  completedCAPAs: number;
  preventedNCRs: number;
  avgResolutionTime: number;
  complianceScore: number;
}

export function QualityDashboard() {
  const [ncrs, setNCRs] = useState<NCRSummary[]>([]);
  const [capas, setCAPAs] = useState<CAPASummary[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [traceabilityItems, setTraceabilityItems] = useState<TraceabilityItem[]>([]);
  const [metrics, setMetrics] = useState<QualityMetrics>({
    totalNCRs: 0,
    openNCRs: 0,
    closedNCRs: 0,
    totalCAPAs: 0,
    overdueCAPAs: 0,
    completedCAPAs: 0,
    preventedNCRs: 0,
    avgResolutionTime: 0,
    complianceScore: 0
  });
  const [selectedNCR, setSelectedNCR] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<NCRStatus | 'ALL' | 'PENDING'>('ALL');
  const [loading, setLoading] = useState(true);
  const [showRCAForm, setShowRCAForm] = useState<string | null>(null);
  const [showCAPAWorkflow, setShowCAPAWorkflow] = useState<string | null>(null);
  const [showCreateNCR, setShowCreateNCR] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [auditEntries, setAuditEntries] = useState<any[]>([]);
  const [knowledgeBase, setKnowledgeBase] = useState<any[]>([]);
  const [showKnowledgeBase, setShowKnowledgeBase] = useState(false);

  // Get API base URL
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';

  // Subscribe to real-time updates
  const { isConnected } = useWebSocket();

  // Load initial data
  useEffect(() => {
    loadQualityData();
  }, []);

  // Load traceability when NCR is selected
  useEffect(() => {
    if (selectedNCR) {
      loadTraceability(selectedNCR);
    }
  }, [selectedNCR, ncrs, capas]);

  // Update metrics when data changes
  useEffect(() => {
    updateMetrics();
  }, [ncrs, capas, alerts]);

  const loadTraceability = async (ncrId: string) => {
    try {
      const ncr = ncrs.find(n => n.ncrId === ncrId);
      if (!ncr) return;

      const items: TraceabilityItem[] = [];

      // Add trigger alerts
      if (ncr.triggerAlertIds && ncr.triggerAlertIds.length > 0) {
        for (const alertId of ncr.triggerAlertIds) {
          items.push({
            id: alertId,
            type: 'alert',
            timestamp: ncr.openedAt,
            title: `Alert ${alertId.substring(0, 8)}`,
            description: 'Triggered NCR creation',
            status: 'RESOLVED',
            machineId: ncr.machineId
          });
        }
      }

      // Add NCR
      items.push({
        id: ncr.ncrId,
        type: 'ncr',
        timestamp: ncr.openedAt,
        title: 'NCR Created',
        description: ncr.impactSummary,
        status: ncr.status,
        machineId: ncr.machineId
      });

      // Add RCA if exists
      const ncrDetail = ncrs.find(n => n.ncrId === ncrId);
      if ((ncrDetail as any)?.rca) {
        items.push({
          id: `${ncrId}-rca`,
          type: 'rca',
          timestamp: (ncrDetail as any).rca.completedAt || ncr.openedAt,
          title: 'RCA Completed',
          description: `Method: ${(ncrDetail as any).rca.method}`,
          status: 'COMPLETED',
          machineId: ncr.machineId,
          assignedTo: (ncrDetail as any).rca.completedBy
        });
      }

      // Add CAPAs
      const ncrCAPAs = capas.filter(c => c.ncrId === ncrId);
      ncrCAPAs.forEach(capa => {
        items.push({
          id: capa.capaId,
          type: 'capa',
          timestamp: capa.createdAt,
          title: `CAPA: ${capa.type}`,
          description: capa.description,
          status: capa.status,
          machineId: ncr.machineId,
          assignedTo: capa.assignedTo
        });
      });

      // Add closure if NCR is closed
      if (ncr.status === 'CLOSED' && ncr.closedAt) {
        items.push({
          id: `${ncrId}-closure`,
          type: 'closure',
          timestamp: ncr.closedAt,
          title: 'NCR Closed',
          description: 'All corrective actions completed',
          status: 'CLOSED',
          machineId: ncr.machineId
        });
      }

      // Sort by timestamp
      items.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      setTraceabilityItems(items);
    } catch (error) {
      console.error('Failed to load traceability:', error);
      setTraceabilityItems([]);
    }
  };

  const loadQualityData = async () => {
    try {
      setLoading(true);
      
      // Load NCRs
      const ncrResponse = await fetch(`${apiBaseUrl}/ncrs`);
      if (!ncrResponse.ok) {
        const errorText = await ncrResponse.text().catch(() => '');
        console.error('NCR API error:', ncrResponse.status, errorText);
        throw new Error(`NCR API returned ${ncrResponse.status}`);
      }
      const ncrData = await ncrResponse.json();
      if (ncrData.success) {
        setNCRs(ncrData.data || []);
      }

      // Load CAPAs
      const capaResponse = await fetch(`${apiBaseUrl}/capas`);
      if (!capaResponse.ok) {
        const errorText = await capaResponse.text().catch(() => '');
        console.error('CAPA API error:', capaResponse.status, errorText);
        throw new Error(`CAPA API returned ${capaResponse.status}`);
      }
      const capaData = await capaResponse.json();
      if (capaData.success) {
        setCAPAs(capaData.data || []);
      }

      // Load Alerts
      const alertResponse = await fetch(`${apiBaseUrl}/alerts`);
      if (alertResponse.ok) {
        const alertData = await alertResponse.json();
        if (alertData.success) {
          setAlerts(alertData.data || []);
        }
      }

      // Load Knowledge Base
      const kbResponse = await fetch(`${apiBaseUrl}/knowledge-base`);
      if (kbResponse.ok) {
        const kbData = await kbResponse.json();
        if (kbData.success) {
          setKnowledgeBase(kbData.data || []);
        }
      }

      setTraceabilityItems([]);
    } catch (error) {
      console.error('Failed to load quality data:', error);
      setNCRs([]);
      setCAPAs([]);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Compute an "effective" NCR status using related CAPA states.
   *
   * Why this exists:
   * - CAPAs can be updated/closed without the NCR record being updated (backend propagation missing)
   * - Or the UI updates CAPA state but does not refresh NCRs, leaving stale NCR.status in memory
   *
   * Behavior:
   * - If NCR is CLOSED -> return CLOSED
   * - Else, if it has CAPAs and *all* of them are CLOSED/COMPLETED -> treat as CLOSED
   * - Otherwise keep NCR.status
   */
  const getEffectiveNCRStatus = (ncr: NCRSummary): NCRStatus => {
    if (ncr.status === 'CLOSED') return 'CLOSED';

    // Find CAPAs linked to this NCR. Prefer CAPA.ncrId when available.
    const linked = capas.filter(c => c.ncrId === ncr.ncrId);
    if (linked.length === 0) return ncr.status;

    const allDone = linked.every(c => c.status === 'CLOSED' || c.status === 'COMPLETED');
    return allDone ? 'CLOSED' : ncr.status;
  };

  const updateMetrics = () => {
    const now = new Date();
    const openNCRs = ncrs.filter(ncr => getEffectiveNCRStatus(ncr) !== 'CLOSED').length;
    const closedNCRs = ncrs.filter(ncr => getEffectiveNCRStatus(ncr) === 'CLOSED').length;
    const overdueCAPAs = capas.filter(capa => 
      capa.status !== 'COMPLETED' && capa.status !== 'CLOSED' && 
      new Date(capa.dueDate) < now
    ).length;
    const completedCAPAs = capas.filter(capa => 
      capa.status === 'COMPLETED' || capa.status === 'CLOSED'
    ).length;

    const closedNCRsWithTime = ncrs.filter(ncr => ncr.closedAt);
    const avgResolutionTime = closedNCRsWithTime.length > 0 
      ? closedNCRsWithTime.reduce((sum, ncr) => {
          const openTime = new Date(ncr.openedAt).getTime();
          const closeTime = new Date(ncr.closedAt!).getTime();
          return sum + (closeTime - openTime);
        }, 0) / closedNCRsWithTime.length / (1000 * 60 * 60 * 24)
      : 0;

    const totalItems = ncrs.length + capas.length;
    const compliantItems = closedNCRs + completedCAPAs;
    const complianceScore = totalItems > 0 ? (compliantItems / totalItems) * 100 : 100;

    // Calculate prevented NCRs from actual data
    const allAlertIds = new Set(ncrs.flatMap(ncr => ncr.triggerAlertIds || []));
    const resolvedAlertsWithoutNCR = alerts.filter(alert => 
      alert.status === 'RESOLVED' && !allAlertIds.has(alert.alertId)
    ).length;
    
    const preventiveCapas = capas.filter(capa => capa.type === 'preventive').length;
    const preventedNCRs = resolvedAlertsWithoutNCR + preventiveCapas;

    setMetrics({
      totalNCRs: ncrs.length,
      openNCRs,
      closedNCRs,
      totalCAPAs: capas.length,
      overdueCAPAs,
      completedCAPAs,
      preventedNCRs,
      avgResolutionTime,
      complianceScore
    });
  };

  const filteredNCRs = ncrs.filter(ncr => {
    const matchesSearch = searchTerm === '' || 
      ncr.ncrId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ncr.machineId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ncr.impactSummary.toLowerCase().includes(searchTerm.toLowerCase());
    
    const effectiveStatus = getEffectiveNCRStatus(ncr);
    const matchesStatus = statusFilter === 'ALL' || 
      (statusFilter === 'PENDING' ? effectiveStatus !== 'CLOSED' : effectiveStatus === statusFilter);
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: NCRStatus) => {
    switch (status) {
      case 'OPEN': return 'text-red-400 bg-red-500/20';
      case 'RCA_PENDING': return 'text-amber-400 bg-amber-500/20';
      case 'CAPA_PENDING': return 'text-blue-400 bg-blue-500/20';
      case 'CLOSED': return 'text-emerald-400 bg-emerald-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getCAPAStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'text-red-400 bg-red-500/20';
      case 'IN_PROGRESS': return 'text-blue-400 bg-blue-500/20';
      case 'COMPLETED': return 'text-emerald-400 bg-emerald-500/20';
      case 'CLOSED': return 'text-slate-400 bg-slate-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isOverdue = (dueDate: string, status: string) => {
    return status !== 'COMPLETED' && status !== 'CLOSED' && new Date(dueDate) < new Date();
  };

  // RCA and CAPA handlers
  const handleRCASubmit = async (ncrId: string, analysis: RCAAnalysis) => {
    try {
      // Be tolerant to different form payload shapes:
      // - { method, analysis }
      // - { method, ...otherFields } (we treat otherFields as analysis)
      const method = (analysis as any).method;
      const derivedAnalysis = (analysis as any).analysis ?? (() => {
        const obj = (analysis as any) || {};
        const { method: _m, ...rest } = obj;
        return Object.keys(rest).length ? rest : obj;
      })();

      const response = await fetch(`${apiBaseUrl}/ncrs/${ncrId}/rca`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ method, analysis: derivedAnalysis })
      });

      if (response.ok) {
        // Update NCR status to CAPA_PENDING
        setNCRs(prev => prev.map(ncr => 
          ncr.ncrId === ncrId 
            ? { ...ncr, status: 'CAPA_PENDING' as NCRStatus }
            : ncr
        ));
        setShowRCAForm(null);
      } else {
        const text = await response.text().catch(() => '');
        console.error('RCA submission failed:', response.status, text);
      }
    } catch (error) {
      console.error('Failed to submit RCA:', error);
    }
  };

  const handleCAPACreate = async (ncrId: string, capaRequest: any) => {
    try {
      const response = await fetch(`${apiBaseUrl}/capas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(capaRequest)
      });

      if (response.ok) {
        const newCapa = await response.json();
        setCAPAs(prev => [...prev, newCapa.data]);
        // Optionally show success message
      }
    } catch (error) {
      console.error('Failed to create CAPA:', error);
    }
  };

  const handleCAPAUpdate = async (capaId: string, updates: any) => {
    try {
      const response = await fetch(`${apiBaseUrl}/capas/${capaId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedCapa = await response.json();
        setCAPAs(prev => prev.map(capa => 
          capa.capaId === capaId ? updatedCapa.data : capa
        ));
      }
    } catch (error) {
      console.error('Failed to update CAPA:', error);
    }
  };

  const handleKnowledgeBaseCreate = async (capaId: string, entry: any) => {
    try {
      const response = await fetch(`${apiBaseUrl}/capas/${capaId}/knowledge-base`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(entry)
      });

      if (response.ok) {
        console.log('Knowledge base entry created successfully');
        // Reload knowledge base to show new entry
        await loadQualityData();
      }
    } catch (error) {
      console.error('Failed to create knowledge base entry:', error);
    }
  };

  const handleCreateNCR = async (ncrData: any) => {
    try {
      console.log('Creating NCR:', ncrData);
      const response = await fetch(`${apiBaseUrl}/ncrs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(ncrData)
      });

      console.log('NCR creation response:', response.status);
      if (response.ok) {
        const newNCR = await response.json();
        console.log('NCR created successfully:', newNCR);
        setNCRs(prev => [newNCR.data, ...prev]);
        setShowCreateNCR(false);
        alert('NCR created successfully!');
      } else {
        const errorText = await response.text().catch(() => '');
        console.error('NCR creation failed:', response.status, errorText);
        alert(`Failed to create NCR: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Failed to create NCR:', error);
      alert(`Error creating NCR: ${error}`);
    }
  };

  const loadAuditTrail = async (resourceId?: string, resourceType?: string) => {
    try {
      const params = new URLSearchParams();
      if (resourceId) params.append('resourceId', resourceId);
      if (resourceType) params.append('resourceType', resourceType);
      params.append('limit', '100');

      const response = await fetch(`${apiBaseUrl}/audit-trail?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const auditData = await response.json();
        setAuditEntries(auditData.data?.auditEntries || []);
      }
    } catch (error) {
      console.error('Failed to load audit trail:', error);
    }
  };

  const handleExportEvidence = async (resourceType: string, resourceId: string, format: string) => {
    try {
      const response = await fetch(`${apiBaseUrl}/evidence/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          resourceType,
          resourceId,
          format,
          includeAuditTrail: true
        })
      });

      if (response.ok) {
        const exportData = await response.json();
        const exportId = exportData.data.exportId;
        
        const downloadResponse = await fetch(`${apiBaseUrl}/evidence/download/${exportId}`);
        if (downloadResponse.ok) {
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `evidence-${resourceId}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          alert('Evidence downloaded successfully!');
        }
      }
    } catch (error) {
      console.error('Failed to export evidence:', error);
      alert('Failed to export evidence');
    }
  };

  if (loading) {
    return (
      <DashboardContentShell>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </DashboardContentShell>
    );
  }

  return (
    <DashboardContentShell>
      {/* Quality Metrics Overview */}
      <KPIBar
        metrics={[
          { label: 'Total NCRs', value: metrics.totalNCRs, status: metrics.openNCRs > 5 ? 'warning' : 'good', icon: <FileText className="w-4 h-4" /> },
          { label: 'Active CAPAs', value: metrics.totalCAPAs - metrics.completedCAPAs, status: metrics.overdueCAPAs > 0 ? 'critical' : 'good', icon: <Clock className="w-4 h-4" /> },
          { label: 'Prevented NCRs', value: metrics.preventedNCRs, status: 'good', icon: <Shield className="w-4 h-4" /> },
          { label: 'Compliance', value: `${metrics.complianceScore.toFixed(1)}%`, status: metrics.complianceScore >= 90 ? 'good' : metrics.complianceScore >= 75 ? 'warning' : 'critical', icon: <CheckCircle className="w-4 h-4" /> },
          { label: 'Status', value: isConnected ? 'LIVE' : 'OFFLINE', status: isConnected ? 'good' : 'critical' },
        ]}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* NCR List and Status Tracking */}
        <DashboardSection 
          title="Non-Conformance Reports"
          subtitle="Quality issue tracking"
          actions={
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setShowCreateNCR(true)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Create NCR</span>
              </button>
              <button 
                onClick={() => setShowKnowledgeBase(true)}
                className="flex items-center space-x-1 px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                title="View Knowledge Base"
              >
                <Shield className="w-4 h-4" />
                <span>Knowledge Base</span>
              </button>
              <button className="p-2 text-slate-400 hover:text-slate-300">
                <Download className="w-4 h-4" />
              </button>
              <button className="p-2 text-slate-400 hover:text-slate-300">
                <Filter className="w-4 h-4" />
              </button>
            </div>
          }
        >
          {/* Search and Filter Controls */}
          <div className="mb-4 space-y-3">
            <div className="flex items-center space-x-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search NCRs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as NCRStatus | 'ALL' | 'PENDING')}
                className="px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="ALL">All Status</option>
                <option value="PENDING">Pending (All Open)</option>
                <option value="OPEN">Open</option>
                <option value="RCA_PENDING">RCA Pending</option>
                <option value="CAPA_PENDING">CAPA Pending</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>
          </div>

          {/* NCR List */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {filteredNCRs.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                No NCRs found matching your criteria
              </div>
            ) : (
              filteredNCRs.map(ncr => (
                <div
                  key={ncr.ncrId}
                  className={`
                    p-4 border rounded-lg cursor-pointer transition-all
                    ${selectedNCR === ncr.ncrId ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600 bg-slate-800'}
                  `}
                  onClick={() => setSelectedNCR(selectedNCR === ncr.ncrId ? null : ncr.ncrId)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h4 className="font-medium text-white break-all">{ncr.ncrId}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatusColor(getEffectiveNCRStatus(ncr))}`}>
                          {getEffectiveNCRStatus(ncr).replace('_', ' ')}
                        </span>
                        <span className="text-sm text-slate-400 whitespace-nowrap">{ncr.machineId}</span>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">{ncr.impactSummary}</p>
                      <div className="flex items-center space-x-4 text-xs text-slate-400">
                        <span>Opened: {formatDate(ncr.openedAt)}</span>
                        <span>{ncr.triggerAlertIds.length} alerts</span>
                        <span>{capas.filter(c => c.ncrId === ncr.ncrId).length} CAPAs</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button 
                        className="p-1 text-slate-400 hover:text-slate-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          loadAuditTrail(ncr.ncrId, 'NCR');
                          setShowAuditTrail(true);
                        }}
                        title="View audit trail"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        className="p-1 text-slate-400 hover:text-slate-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportEvidence('NCR', ncr.ncrId, 'PDF');
                        }}
                        title="Export evidence"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {ncr.status === 'OPEN' && (
                        <button 
                          className="px-2 py-1 text-xs bg-amber-500/20 text-amber-400 rounded hover:bg-amber-500/30 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowRCAForm(ncr.ncrId);
                          }}
                        >
                          Start RCA
                        </button>
                      )}
                      {ncr.status === 'CAPA_PENDING' && (
                        <button 
                          className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCAPAWorkflow(ncr.ncrId);
                          }}
                        >
                          Manage CAPAs
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DashboardSection>

        {/* CAPA Workflow Display */}
        <DashboardWidget title="Corrective & Preventive Actions">
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {capas.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                No CAPAs found
              </div>
            ) : (
              capas.map(capa => (
                <div
                  key={capa.capaId}
                  className="p-3 bg-slate-800 border border-slate-700 rounded-lg"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCAPAStatusColor(capa.status)}`}>
                        {capa.status.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-slate-400 uppercase">{capa.type}</span>
                    </div>
                    {isOverdue(capa.dueDate, capa.status) && (
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    )}
                  </div>
                  
                  <h5 className="font-medium text-sm text-white mb-1">
                    {capa.description}
                  </h5>
                  
                  <div className="text-xs text-slate-400 space-y-1">
                    <div>Assigned to: {capa.assignedTo}</div>
                    <div className={isOverdue(capa.dueDate, capa.status) ? 'text-red-400' : ''}>
                      Due: {formatDate(capa.dueDate)}
                    </div>
                    <div>NCR: {capa.ncrId}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DashboardWidget>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Knowledge Base */}
        <DashboardSection title="Knowledge Base" subtitle="Lessons learned from CAPAs">
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {knowledgeBase.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No knowledge base entries yet</p>
              </div>
            ) : (
              knowledgeBase.map((entry) => (
                <div key={entry.id} className="p-3 bg-slate-800 border border-slate-700 rounded-lg hover:border-slate-600 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <h5 className="font-medium text-sm text-white">{entry.title}</h5>
                    <span className="px-2 py-1 text-xs bg-indigo-500/20 text-indigo-400 rounded">
                      {entry.category}
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 mb-2 line-clamp-2">{entry.content}</p>
                  {entry.tags && entry.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {entry.tags.map((tag: string) => (
                        <span key={tag} className="px-1.5 py-0.5 text-xs bg-slate-700 text-slate-400 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-slate-400">
                    {formatDate(entry.createdAt)} • {entry.createdBy}
                  </div>
                </div>
              ))
            )}
          </div>
        </DashboardSection>

        {/* Alert-to-Closure Traceability */}
        <DashboardSection title="Alert-to-Closure Traceability" subtitle="End-to-end tracking">
          <div className="space-y-4">
            {selectedNCR ? (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="text-sm text-slate-400 flex-1 min-w-0">
                    <span>Traceability for NCR: </span>
                    <span className="font-medium text-white break-all">{selectedNCR}</span>
                  </div>
                  <button
                    onClick={() => {
                      const ncr = ncrs.find(n => n.ncrId === selectedNCR);
                      if (!ncr) return;
                      
                      let content = `ALERT-TO-CLOSURE TRACEABILITY REPORT\n`;
                      content += `${'='.repeat(60)}\n\n`;
                      content += `NCR ID: ${selectedNCR}\n`;
                      content += `Machine: ${ncr.machineId}\n`;
                      content += `Status: ${ncr.status}\n`;
                      content += `Impact: ${ncr.impactSummary}\n`;
                      content += `Generated: ${new Date().toISOString()}\n\n`;
                      content += `${'='.repeat(60)}\n\n`;
                      
                      traceabilityItems.forEach((item, idx) => {
                        content += `${idx + 1}. ${item.title.toUpperCase()}\n`;
                        content += `   Type: ${item.type}\n`;
                        content += `   Timestamp: ${item.timestamp}\n`;
                        content += `   Status: ${item.status}\n`;
                        content += `   Description: ${item.description}\n`;
                        if (item.assignedTo) content += `   Assigned To: ${item.assignedTo}\n`;
                        content += `\n`;
                      });
                      
                      const blob = new Blob([content], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `traceability-${selectedNCR}.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                    title="Download traceability report"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
                
                {/* Traceability Timeline */}
                <div className="space-y-3">
                  {traceabilityItems.length === 0 ? (
                    <div className="text-center py-4 text-slate-400 text-sm">
                      Loading traceability data...
                    </div>
                  ) : (
                    traceabilityItems.map((item, index) => (
                      <div key={item.id} className="flex items-start space-x-3">
                        <div className="flex flex-col items-center">
                          <div className={`
                            w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium
                            ${item.type === 'alert' ? 'bg-red-500/20 text-red-400' :
                              item.type === 'ncr' ? 'bg-amber-500/20 text-amber-400' :
                              item.type === 'rca' ? 'bg-blue-500/20 text-blue-400' :
                              item.type === 'capa' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-emerald-500/20 text-emerald-400'}
                          `}>
                            {item.type === 'alert' ? 'A' :
                             item.type === 'ncr' ? 'N' :
                             item.type === 'rca' ? 'R' :
                             item.type === 'capa' ? 'C' : '✓'}
                          </div>
                          {index < traceabilityItems.length - 1 && (
                            <div className="w-px h-6 bg-slate-600 mt-1"></div>
                          )}
                        </div>
                        
                        <div className="flex-1 pb-4">
                          <div className="flex items-center space-x-2 mb-1">
                            <h6 className="font-medium text-sm text-white">{item.title}</h6>
                            <span className="text-xs text-slate-400">{formatDate(item.timestamp)}</span>
                          </div>
                          <p className="text-sm text-slate-300 mb-1">{item.description}</p>
                          <div className="flex items-center space-x-3 text-xs text-slate-400">
                            <span>Status: {item.status}</span>
                            <span>Machine: {item.machineId}</span>
                            {item.assignedTo && <span>Assigned: {item.assignedTo}</span>}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <ArrowRight className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Select an NCR to view its complete traceability chain
              </div>
            )}
          </div>
        </DashboardSection>

        {/* Prevented NCR Counters */}
        <DashboardWidget title="Prevention Metrics">
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-400 mb-1">
                {metrics.preventedNCRs}
              </div>
              <div className="text-sm text-slate-300">NCRs Prevented</div>
              <div className="text-xs text-slate-400 mt-1">This Month</div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">Early Detection</span>
                <span className="font-medium text-white">
                  {(() => {
                    const allAlertIds = new Set(ncrs.flatMap(ncr => ncr.triggerAlertIds || []));
                    return alerts.filter(alert => alert.status === 'RESOLVED' && !allAlertIds.has(alert.alertId)).length;
                  })()}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">Proactive Maintenance</span>
                <span className="font-medium text-white">
                  {capas.filter(capa => capa.type === 'preventive').length}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">Process Improvement</span>
                <span className="font-medium text-white">
                  {knowledgeBase.length}
                </span>
              </div>
            </div>
            
            <div className="pt-3 border-t border-slate-700">
              <div className="flex items-center space-x-2 text-sm text-emerald-400">
                <TrendingDown className="w-4 h-4" />
                <span>
                  {(() => {
                    const total = ncrs.length + metrics.preventedNCRs;
                    const reduction = total > 0 ? ((metrics.preventedNCRs / total) * 100).toFixed(0) : 0;
                    return `${reduction}% reduction in NCR rate`;
                  })()}
                </span>
              </div>
            </div>
          </div>
        </DashboardWidget>
      </div>
      {!isConnected && (
        <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-amber-400">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">Real-time updates unavailable - working in offline mode</span>
          </div>
        </div>
      )}

      {/* RCA Analysis Form Modal */}
      {showRCAForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">Root Cause Analysis</h2>
              <button
                onClick={() => setShowRCAForm(null)}
                className="text-slate-400 hover:text-slate-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <RCAAnalysisForm
                ncrId={showRCAForm}
                onSubmit={(analysis) => handleRCASubmit(showRCAForm, analysis)}
                onCancel={() => setShowRCAForm(null)}
                loading={loading}
              />
            </div>
          </div>
        </div>
      )}

      {/* CAPA Workflow Modal */}
      {showCAPAWorkflow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">CAPA Management</h2>
              <button
                onClick={() => setShowCAPAWorkflow(null)}
                className="text-slate-400 hover:text-slate-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <CAPAWorkflow
                ncrId={showCAPAWorkflow}
                existingCAPAs={capas.filter(capa => capa.ncrId === showCAPAWorkflow).map(capa => ({
                  ...capa,
                  pk: `TENANT#demo-tenant`,
                  sk: `CAPA#${capa.capaId}`,
                  tenantId: 'demo-tenant'
                }))}
                onCAPACreate={(capaRequest) => handleCAPACreate(showCAPAWorkflow, capaRequest)}
                onCAPAUpdate={handleCAPAUpdate}
                onKnowledgeBaseCreate={handleKnowledgeBaseCreate}
                loading={loading}
              />
            </div>
          </div>
        </div>
      )}

      {/* Create NCR Modal */}
      {showCreateNCR && (
        <CreateNCRModal
          onSubmit={handleCreateNCR}
          onCancel={() => setShowCreateNCR(false)}
          loading={loading}
        />
      )}

      {/* Audit Trail Modal */}
      {showAuditTrail && (
        <AuditTrailModal
          auditEntries={auditEntries}
          onClose={() => setShowAuditTrail(false)}
        />
      )}

      {/* Knowledge Base Modal */}
      {showKnowledgeBase && (
        <KnowledgeBaseModal
          knowledgeBase={knowledgeBase}
          onClose={() => setShowKnowledgeBase(false)}
        />
      )}
    </DashboardContentShell>
  );
}

// Create NCR Modal Component
interface CreateNCRModalProps {
  onSubmit: (ncrData: any) => void;
  onCancel: () => void;
  loading: boolean;
}

function CreateNCRModal({ onSubmit, onCancel, loading }: CreateNCRModalProps) {
  const [formData, setFormData] = useState({
    machineId: '',
    impactSummary: '',
    triggerAlertIds: [] as string[],
    alertIdInput: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.machineId.trim() || !formData.impactSummary.trim()) {
      return;
    }

    onSubmit({
      machineId: formData.machineId,
      impactSummary: formData.impactSummary,
      triggerAlertIds: formData.triggerAlertIds
    });
  };

  const addAlertId = () => {
    if (formData.alertIdInput.trim() && !formData.triggerAlertIds.includes(formData.alertIdInput.trim())) {
      setFormData(prev => ({
        ...prev,
        triggerAlertIds: [...prev.triggerAlertIds, prev.alertIdInput.trim()],
        alertIdInput: ''
      }));
    }
  };

  const removeAlertId = (alertId: string) => {
    setFormData(prev => ({
      ...prev,
      triggerAlertIds: prev.triggerAlertIds.filter(id => id !== alertId)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-2xl w-full">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Create Non-Conformance Report</h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Machine ID *
            </label>
            <input
              type="text"
              value={formData.machineId}
              onChange={(e) => setFormData(prev => ({ ...prev, machineId: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter machine ID"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Impact Summary *
            </label>
            <textarea
              value={formData.impactSummary}
              onChange={(e) => setFormData(prev => ({ ...prev, impactSummary: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe the impact and non-conformance..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Trigger Alert IDs (Optional)
            </label>
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={formData.alertIdInput}
                onChange={(e) => setFormData(prev => ({ ...prev, alertIdInput: e.target.value }))}
                className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter alert ID"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAlertId())}
              />
              <button
                type="button"
                onClick={addAlertId}
                className="px-3 py-2 bg-slate-700 text-slate-300 rounded-md hover:bg-slate-600 transition-colors"
              >
                Add
              </button>
            </div>
            
            {formData.triggerAlertIds.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.triggerAlertIds.map((alertId) => (
                  <span
                    key={alertId}
                    className="inline-flex items-center px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded"
                  >
                    {alertId}
                    <button
                      type="button"
                      onClick={() => removeAlertId(alertId)}
                      className="ml-1 text-blue-400 hover:text-blue-300"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-slate-300 bg-slate-700 rounded-md hover:bg-slate-600 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.machineId.trim() || !formData.impactSummary.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create NCR'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
// Audit Trail Modal Component
interface AuditTrailModalProps {
  auditEntries: any[];
  onClose: () => void;
}

function AuditTrailModal({ auditEntries, onClose }: AuditTrailModalProps) {
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE_NCR': return <Plus className="w-4 h-4 text-blue-400" />;
      case 'SUBMIT_RCA': return <Search className="w-4 h-4 text-amber-400" />;
      case 'CREATE_CAPA': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'UPDATE_CAPA_STATUS': return <Edit className="w-4 h-4 text-purple-400" />;
      case 'LINK_EVIDENCE': return <FileText className="w-4 h-4 text-orange-400" />;
      case 'EXPORT_EVIDENCE': return <Download className="w-4 h-4 text-slate-400" />;
      case 'CREATE_KNOWLEDGE_BASE_ENTRY': return <Shield className="w-4 h-4 text-indigo-400" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Audit Trail</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 max-h-[calc(90vh-120px)] overflow-y-auto">
          {auditEntries.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No audit entries found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {auditEntries.map((entry) => (
                <div key={entry.id} className="bg-slate-900 border border-slate-600 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      {getActionIcon(entry.action)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-white">
                          {formatAction(entry.action)}
                        </h4>
                        <span className="text-sm text-slate-400">
                          {formatDate(entry.timestamp)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-300">
                        <div>
                          <span className="font-medium text-slate-400">Resource:</span> {entry.resourceType} - {entry.resourceId}
                        </div>
                        <div>
                          <span className="font-medium text-slate-400">User:</span> {entry.userId}
                        </div>
                        {entry.ipAddress && (
                          <div>
                            <span className="font-medium text-slate-400">IP Address:</span> {entry.ipAddress}
                          </div>
                        )}
                        {entry.userAgent && (
                          <div className="md:col-span-2">
                            <span className="font-medium text-slate-400">User Agent:</span> {entry.userAgent}
                          </div>
                        )}
                      </div>
                      
                      {entry.details && Object.keys(entry.details).length > 0 && (
                        <div className="mt-3 p-3 bg-slate-800 border border-slate-600 rounded">
                          <span className="font-medium text-sm text-slate-300">Details:</span>
                          <pre className="mt-1 text-xs text-slate-400 whitespace-pre-wrap">
                            {JSON.stringify(entry.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-end p-4 border-t border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 text-slate-300 rounded-md hover:bg-slate-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Knowledge Base Modal Component
interface KnowledgeBaseModalProps {
  knowledgeBase: any[];
  onClose: () => void;
}

function KnowledgeBaseModal({ knowledgeBase, onClose }: KnowledgeBaseModalProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-indigo-400" />
            <h2 className="text-lg font-semibold text-white">Knowledge Base</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 max-h-[calc(90vh-120px)] overflow-y-auto">
          {knowledgeBase.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg">No knowledge base entries yet</p>
              <p className="text-sm mt-2">Complete CAPAs and add lessons learned to build your knowledge base</p>
            </div>
          ) : (
            <div className="space-y-4">
              {knowledgeBase.map((entry) => (
                <div key={entry.id} className="bg-slate-900 border border-slate-600 rounded-lg p-4 hover:border-slate-500 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-medium text-white">{entry.title}</h3>
                    <span className="px-3 py-1 text-xs bg-indigo-500/20 text-indigo-400 rounded-full">
                      {entry.category}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 mb-3 whitespace-pre-wrap">{entry.content}</p>
                  {entry.tags && entry.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {entry.tags.map((tag: string) => (
                        <span key={tag} className="px-2 py-1 text-xs bg-slate-700 text-slate-400 rounded">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-slate-400 pt-3 border-t border-slate-700">
                    <span>Created by {entry.createdBy}</span>
                    <span>{formatDate(entry.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-end p-4 border-t border-slate-700">
          <button onClick={onClose} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-md hover:bg-slate-600 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}